import {
    ChatMessage,
    ChatMessageType,
    IWebRTCReceiverType,
    KurentoRequest,
    KurentoRequestIds,
    KurentoResponse,
    Unread,
    VideoType,
    WebRTCReceiver,
    NotificationsPlayer,
} from '_helpers';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';

const initialState: State = {
    messages: [],
    webRTC: {},
    acceptedApplications: [],
    connectionQueue: [],
    unread: {},
    openedOnlineModal: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setMessage(state, message: ChatMessage): State {
            return { ...state, messages: [...state.messages, message] };
        },
        setMessages(state, messages: ChatMessage[]): State {
            return { ...state, messages };
        },

        setOpenedOnlineModal(state, openedOnlineModal: string | null): State {
            return { ...state, openedOnlineModal };
        },

        setAcceptedApplications(state, acceptedApplications: string[]): State {
            return { ...state, acceptedApplications };
        },

        setConnectionQueue(state, connectionQueue: IConnectionQueueItemType[]): State {
            return { ...state, connectionQueue };
        },

        addToConnectionQueue(state, item: IConnectionQueueItemType): State {
            return { ...state, connectionQueue: [...state.connectionQueue, item] };
        },

        shiftFromConnectionQueue(state): State {
            return { ...state, connectionQueue: state.connectionQueue.slice(1) };
        },

        setUnread(state, unread: { [id: string]: Unread }): State {
            return { ...state, unread };
        },

        updateWebRTC(state): State {
            return { ...state, webRTC: { ...state.webRTC } };
        },

        addApplicantReceiver(state, applicantReceiver: {
            receiver: IWebRTCReceiverType;
            connected: boolean;
            presenterId: string;
        }): State {
            const newWebRTC = {
                ...state.webRTC,
                [applicantReceiver.presenterId]: {
                    ...applicantReceiver,
                    type: VideoType.Cam,
                },
            };
            (window as any).webRTC = newWebRTC;

            return {
                ...state,
                webRTC: newWebRTC,
            };
        },

        removeApplicantReceiver(state, presenterId: string): State {
            const newWebRTC = { ...state.webRTC };
            delete newWebRTC[presenterId];

            return {
                ...state,
                webRTC: newWebRTC,
            };
        },

        clearWebRTC(state): State {
            return {
                ...state,
                webRTC: { },
            };
        },

        setConnected(state, presenterId: string): State {
            const webRTC = { ...state.webRTC };
            webRTC[presenterId].connected = true;
            return { ...state, webRTC };
        },
        setDisconnected(state, presenterId: string): State {
            const webRTC = { ...state.webRTC };
            webRTC[presenterId].connected = false;
            return { ...state, webRTC };
        },

        setType(state, { presenterId, videoType }): State {
            const newWebRTC = { ...state.webRTC };
            if (newWebRTC[presenterId]) {
                newWebRTC[presenterId] = {
                    ...newWebRTC[presenterId],
                    type: videoType,
                };
            }
            return {
                ...state,
                webRTC: newWebRTC,
            };
        },

        setCurrentConnection(state, currentConnection: IConnectionQueueItemType): State {
            return { ...state, currentConnection };
        },
    },
    effects: () => ({
        async init() {
            await apiConnector.sendSocketMessage(socket => socket.init());
        },

        async getMessagesAsync(presenterId: string) {
            const res = await apiConnector.sendSocketMessage(socket => socket.getChatMessages(presenterId));

            return res;
        },

        async handleNewMessageAsync(message: ChatMessage, rootState) {
            const { openedOnlineModal } = rootState.online as State;
            this.setMessage(message);

            if (!message.fromApplicant) return;
            await new NotificationsPlayer(
              message.type === ChatMessageType.Message,
              message.type === ChatMessageType.Violation,
              openedOnlineModal === message.applicantId,
            ).play();
        },

        async markAsRead(presenterId: string) {
            await apiConnector.sendSocketMessage(socket => socket.markAsRead(presenterId));
        },

        async sendMessageAsync(message: { applicantId: string; message: string }) {
            await apiConnector.sendSocketMessage(socket => socket.sendChatMessageToApplicantExam(
              ChatMessageType.Message,
              message.applicantId,
              message.message,
            ));
        },

        async handlePeerConnectionConnect(data: {presenterId: string; videoType?: VideoType}) {
            this.addToQueue({
                presenterId: data.presenterId,
                videoType: data.videoType || VideoType.Cam,
                func: this.startReceiver,
            });
        },

        async handlePeerConnectionReconnect(data: {presenterId: string; videoType: VideoType}) {
            this.addToQueue({
                presenterId: data.presenterId,
                videoType: data.videoType,
                func: this.restartReceiver,
            });
        },

        async startReceiver(data: { presenterId: string }) {
            const newApplicantReceiver = new WebRTCReceiver();
            newApplicantReceiver.onEvent = this.handlePeerConnectionEvents;
            newApplicantReceiver.onError = this.handlePeerConnectionErrors;
            newApplicantReceiver.onReconnect = this.handlePeerConnectionReconnect;
            newApplicantReceiver.onUpdateWebRTC = this.updateWebRTC;

            await newApplicantReceiver.start(data.presenterId);

            newApplicantReceiver.subscribeToConnectEvent(this.handlePeerConnectionConnected);

            this.addApplicantReceiver({
                receiver: newApplicantReceiver,
                connected: false,
                presenterId: data.presenterId,
            });
        },

        async restartReceiver(data: { presenterId: string; videoType: VideoType }, rootState) {
            const { webRTC } = rootState.online as State;

            if (!webRTC[data.presenterId]) {
                await this.startReceiver(data);
                return;
            }

            const { receiver } = webRTC[data.presenterId];

            receiver.onEvent = this.handlePeerConnectionEvents;
            receiver.onError = this.handlePeerConnectionErrors;
            receiver.onReconnect = this.handlePeerConnectionReconnect;

            await receiver.start(data.presenterId);

            receiver.subscribeToConnectEvent(this.handlePeerConnectionConnected);

            this.addApplicantReceiver({
                receiver,
                connected: false,
                presenterId: data.presenterId,
            });
        },

        async startConnectionForApplicantIds(applicantsIds: string[], rootState) {
            const { currentConnection } = rootState.online as State;

            const newConnectionQueue = applicantsIds.map(id => ({
                presenterId: id,
                type: VideoType.Cam,
                func: this.startReceiver,
            }));

            this.setConnectionQueue(newConnectionQueue);

            if (!currentConnection) {
                this.nextQueueItem();
            }
        },

        async addToQueue(item: IConnectionQueueItemType, rootState) {
            const { currentConnection } = rootState.online as State;

            this.addToConnectionQueue(item);

            if (!currentConnection) {
                this.nextQueueItem();
            }
        },

        async nextQueueItem(_, rootState) {
            const { connectionQueue, webRTC } = rootState.online as State;
            const currentConnection = connectionQueue[0];

            if (!currentConnection) {
                this.setCurrentConnection(undefined);
                return;
            }
            this.shiftFromConnectionQueue();

            if (webRTC[currentConnection.presenterId] && webRTC[currentConnection.presenterId].connected) {
                this.nextQueueItem();
                return;
            }

            this.setCurrentConnection(currentConnection);
            currentConnection.func({
                videoType: currentConnection.type,
                presenterId: currentConnection.presenterId,
            });
        },

        async handlePeerConnectionConnected(_, rootState) {
            const { currentConnection, webRTC } = rootState.online as State;

            if (currentConnection && webRTC[currentConnection.presenterId] && webRTC[currentConnection.presenterId].receiver) {
                delete webRTC[currentConnection.presenterId].receiver.onEvent;
                this.setConnected(currentConnection.presenterId);
            }

            this.nextQueueItem();
        },

        async handleKurentoMessage(data: KurentoResponse, rootState) {
            const { webRTC, currentConnection } = rootState.online as State;

            if (currentConnection && webRTC[currentConnection.presenterId] && data.presenterId === currentConnection.presenterId) {
                await webRTC[data.presenterId].receiver.handleMessage(data);
            }
        },

        async handlePeerConnectionEvents(message: KurentoRequest) {
            await apiConnector.sendSocketMessage(socket => socket.sendKurentoMessage(message));
        },

        async handlePeerConnectionErrors(data: {videoType: VideoType; presenterId?: string}) {
            if (data.presenterId) {
                this.setDisconnected(data.presenterId);
            }
            this.nextQueueItem();
        },

        async switchVideoType(data: { videoType: VideoType; presenterId: string }) {
            this.setType(data);
            await apiConnector.sendSocketMessage(socket => socket.sendKurentoMessage({
                id: KurentoRequestIds.Switch,
                videoType: data.videoType,
                presenterId: data.presenterId,
            }));
        },

        async stopReceiver(presenterId: string, rootState) {
            if (!rootState.online.webRTC[presenterId]) return;

            const { receiver } = rootState.online.webRTC[presenterId];
            await receiver.stop();

            this.removeApplicantReceiver(presenterId);
        },

        async stopAll(_, rootState) {
            const { webRTC } = rootState.online as State;
            await Promise.all(Object.keys(webRTC).map((k) => {
                const rec = webRTC[k].receiver;
                return rec.stop();
            }));

            this.clearWebRTC();
        },
    }),
});

interface State {
    messages: ChatMessage[];
    acceptedApplications: string[];
    connectionQueue: IConnectionQueueItemType[];
    currentConnection?: IConnectionQueueItemType;
    unread: { [id: string]: Unread };
    webRTC: {
        [id: string]: {
            receiver: IWebRTCReceiverType;
            connected: boolean;
            type: VideoType;
        };
    };
    openedOnlineModal: string | null;
}

interface IConnectionQueueItemType {
    presenterId: string;
    type: VideoType;
    func: (data: { presenterId: string; videoType: VideoType }) => void;
}
